import { useState, useEffect, useRef } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { ensureProfile } from '@/lib/ensureProfile'
import { useAuth } from '@/contexts/AuthContext'

interface CreateGroupModalProps {
  onClose: () => void
  onSuccess: () => void
}

export default function CreateGroupModal({ onClose, onSuccess }: CreateGroupModalProps) {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const [groupName, setGroupName] = useState('')
  const [description, setDescription] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const nameInputRef = useRef<HTMLInputElement>(null)

  // Focus first input on mount
  useEffect(() => {
    nameInputRef.current?.focus()
  }, [])

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isSubmitting) {
        onClose()
      }
    }
    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [onClose, isSubmitting])

  const createGroup = useMutation({
    mutationFn: async (data: { name: string; description: string | null }) => {
      if (!user) throw new Error('Not authenticated')
      await ensureProfile(user)

      // Create the group
      const { data: group, error: groupError } = await supabase
        .from('groups')
        .insert({
          name: data.name,
          description: data.description || null,
          owner_id: user.id,
        })
        .select()
        .single()

      if (groupError) throw groupError

      // Add user as owner in group_members
      const { error: memberError } = await supabase
        .from('group_members')
        .insert({
          group_id: group.id,
          user_id: user.id,
          role: 'owner',
        })

      if (memberError) throw memberError

      return group
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-groups', user?.id] })
      queryClient.invalidateQueries({ queryKey: ['other-groups', user?.id] })
      queryClient.invalidateQueries({ queryKey: ['user-groups', user?.id] })
      queryClient.invalidateQueries({ queryKey: ['user-groups-sidebar', user?.id] })
      onSuccess()
    },
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    // Basic validation
    if (!groupName.trim()) {
      setError('Group name is required')
      return
    }

    if (groupName.trim().length < 3) {
      setError('Group name must be at least 3 characters')
      return
    }

    setIsSubmitting(true)
    try {
      await createGroup.mutateAsync({
        name: groupName.trim(),
        description: description.trim() || null,
      })
    } catch (err: any) {
      setError(err.message || 'Failed to create group')
      setIsSubmitting(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="create-group-title"
    >
      <div
        className="rounded-lg p-6 max-w-md w-full"
        style={{
          backgroundColor: 'var(--bg-secondary)',
          border: '2px solid var(--bg-tertiary)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <h2 
            id="create-group-title"
            className="text-2xl font-bold"
            style={{ color: 'var(--text-primary)' }}
          >
            Create New Group
          </h2>
          <button
            onClick={onClose}
            className="p-2"
            aria-label="Close dialog"
            style={{ color: 'var(--text-primary)' }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Group Name Field */}
          <div>
            <label 
              htmlFor="group-name"
              className="block mb-2 font-semibold"
              style={{ color: 'var(--text-primary)' }}
            >
              Group Name
              <span style={{ color: 'var(--color-error)' }} aria-label="required"> *</span>
            </label>
            <input
              ref={nameInputRef}
              type="text"
              id="group-name"
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
              placeholder="Enter group name"
              required
              minLength={3}
              className="w-full px-4 py-2 border-2 rounded"
              style={{
                borderColor: 'var(--bg-tertiary)',
                backgroundColor: 'var(--bg-primary)',
                color: 'var(--text-primary)',
              }}
              aria-required="true"
            />
          </div>

          {/* Description Field */}
          <div>
            <label 
              htmlFor="group-description"
              className="block mb-2 font-semibold"
              style={{ color: 'var(--text-primary)' }}
            >
              Description (Optional)
            </label>
            <textarea
              id="group-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe your group"
              rows={4}
              className="w-full px-4 py-2 border-2 rounded"
              style={{
                borderColor: 'var(--bg-tertiary)',
                backgroundColor: 'var(--bg-primary)',
                color: 'var(--text-primary)',
              }}
            />
          </div>

          {error && (
            <div 
              role="alert"
              className="p-3 rounded"
              style={{
                backgroundColor: 'var(--color-error)',
                color: 'var(--text-inverse)',
              }}
            >
              {error}
            </div>
          )}

          {/* Buttons */}
          <div className="flex gap-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 font-semibold rounded"
              style={{
                backgroundColor: 'var(--bg-tertiary)',
                color: 'var(--text-primary)',
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 px-4 py-2 font-semibold rounded disabled:opacity-50"
              style={{
                backgroundColor: 'var(--bg-interactive)',
                color: 'var(--text-inverse)',
              }}
            >
              {isSubmitting ? 'Creating...' : 'Create Group'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

